import { SCHOOL_API_BASE_URL } from './schoolProfileApi';
import { normalizePublicPath } from '../utils/reservedPaths';

// Public online admission submission. The browser never touches Firebase
// Storage: the whole application, including files, is posted to the backend as
// multipart/form-data and the backend stores the files itself.

const REQUEST_TIMEOUT_MS = 120000;

/** The application could not be accepted as filled in — message is user-safe. */
export class AdmissionValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AdmissionValidationError';
    this.code = 'INVALID';
  }
}

/** The school's public page no longer exists. */
export class AdmissionSchoolNotFoundError extends Error {
  constructor() {
    super('This school is no longer accepting applications online.');
    this.name = 'AdmissionSchoolNotFoundError';
    this.code = 'NOT_FOUND';
  }
}

/** Network, timeout or server failure — the form is safe to resubmit. */
export class AdmissionRequestError extends Error {
  constructor(message) {
    super(message || 'We could not submit this application. Please try again.');
    this.name = 'AdmissionRequestError';
    this.code = 'REQUEST_FAILED';
  }
}

const buildUrl = (publicPath) =>
  `${SCHOOL_API_BASE_URL}/school/public-profile/${encodeURIComponent(publicPath)}/admission`;

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

/**
 * Shape the form state into the payload the backend accepts. Fields the server
 * owns — school id, admission type, enrollment status and source, academic year
 * and quarter — are deliberately absent: it sets them itself.
 */
export const buildAdmissionPayload = (values) => ({
  studentName: values.studentName.trim(),
  gender: values.gender,
  dateOfBirth: values.dateOfBirth ? Math.floor(new Date(values.dateOfBirth).getTime() / 1000) : 0,
  address: values.address.trim(),
  nationality: values.nationality.trim(),
  previousSchool: values.previousSchool.trim(),
  preferredStartDate: values.preferredStartDate
    ? Math.floor(new Date(values.preferredStartDate).getTime() / 1000)
    : 0,

  specialNeeds: values.specialNeeds.trim(),
  medicalConditions: values.medicalConditions.trim(),
  allergens: values.allergens.trim(),

  parentName: values.parentName.trim(),
  parentRelationshipWithStudent: values.parentRelationship,
  parentPhoneNumber: values.parentPhone.trim(),
  parentEmail: values.parentEmail.trim(),
  parentOccupation: values.parentOccupation.trim(),

  hasSecondParent: values.hasSecondParent,
  parentTwoName: values.hasSecondParent ? values.parentTwoName.trim() : '',
  parentTwoRelationshipWithStudent: values.hasSecondParent ? values.parentTwoRelationship : '',
  parentTwoPhoneNumber: values.hasSecondParent ? values.parentTwoPhone.trim() : '',
  parentTwoEmail: values.hasSecondParent ? values.parentTwoEmail.trim() : '',
  parentTwoOccupation: values.hasSecondParent ? values.parentTwoOccupation.trim() : '',

  admissionNote: values.admissionNote.trim(),
});

/**
 * Submit an application for a school's public path.
 *
 * Resolves when the backend confirms the application was stored. Rejects with
 * a typed error carrying a message that is safe to show the parent.
 *
 * @param {string} rawPublicPath school public path, e.g. "queens"
 * @param {object} values form state
 * @param {{ profileImage: File|null, documents: {file: File}[] }} files
 * @param {{ signal?: AbortSignal }} [options]
 */
export const submitPublicAdmission = async (rawPublicPath, values, files, { signal } = {}) => {
  const publicPath = normalizePublicPath(rawPublicPath);
  if (!publicPath) throw new AdmissionSchoolNotFoundError();

  const body = new FormData();
  body.append('data', JSON.stringify(buildAdmissionPayload(values)));

  if (files.profileImage) {
    body.append('profileImage', files.profileImage, files.profileImage.name);
  }
  files.documents.forEach(({ file }) => {
    body.append('documents', file, file.name);
  });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const forwardAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) forwardAbort();
    signal.addEventListener('abort', forwardAbort);
  }

  let response;
  try {
    response = await fetch(buildUrl(publicPath), {
      method: 'POST',
      // No Content-Type header: the browser sets the multipart boundary.
      body,
      signal: controller.signal,
    });
  } catch (error) {
    if (signal && signal.aborted) throw error;
    throw new AdmissionRequestError(
      'We could not reach the school. Check your connection and try again.'
    );
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', forwardAbort);
  }

  const payload = await readJson(response);
  const message = payload && typeof payload.errDesc === 'string' ? payload.errDesc : '';

  if (response.status === 404) throw new AdmissionSchoolNotFoundError();
  if (response.status === 429) {
    throw new AdmissionRequestError(
      message || 'Too many applications from this device. Please try again later.'
    );
  }
  if (response.status === 400 || response.status === 413) {
    throw new AdmissionValidationError(
      message || 'Some answers need checking before this can be submitted.'
    );
  }
  if (!response.ok) throw new AdmissionRequestError(message);

  if (!payload || payload.status !== 'success') {
    throw new AdmissionRequestError(message);
  }

  return true;
};
